import { LineChart } from "@mui/x-charts/LineChart";
import { DBAccount, DBPosition } from "../../../../api/types";
import { AxisValueFormatterContext } from "@mui/x-charts";
import { useCreateGraphFromPositions } from "./graph";

export default function AccountBalanceGraph({
  account,
  positions,
  rangeStartTs,
  extendToRangeStart,
}: {
  account: DBAccount;
  positions: DBPosition[];
  rangeStartTs?: number;
  extendToRangeStart?: boolean;
}) {
  const graphData = useCreateGraphFromPositions({
    positions,
    rangeStartTs,
    extendToRangeStart,
    minDistanceInMinutes: 5,
    positionEntryCallback: (dataset, position, minTs) => {
      const { openTransaction, closeTransaction } = position;

      if (openTransaction?.filledTimestamp && openTransaction?.totalEquity) {
        const timestamp = new Date(openTransaction.filledTimestamp).getTime();
        minTs = Math.min(minTs, timestamp);
        dataset.push({
          x: timestamp,
          y: openTransaction.totalEquity,
        });
      }

      if (closeTransaction?.filledTimestamp && closeTransaction?.totalEquity) {
        const timestamp = new Date(closeTransaction.filledTimestamp).getTime();
        dataset.push({
          x: timestamp,
          y: closeTransaction.totalEquity,
        });
      }

      return { minTs };
    },
    lastEntryCallback: (dataset) => {
      dataset.push({
        x: Date.now(),
        y: account.totalEquity,
      });
    },
  });

  return (
    <LineChart
      height={300}
      xAxis={[
        {
          dataKey: "x",
          valueFormatter: (
            value: number,
            context: AxisValueFormatterContext,
          ) => {
            const date = new Date(value);
            const shortDate = date.toLocaleDateString("nl-NL", {
              month: "short",
              day: "numeric",
            });

            const longDate = date.toLocaleTimeString("nl-NL", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });

            if (context.location === "tick") {
              return shortDate;
            }

            const position = positions.find((pos) => {
              const OTS = pos.openTransaction?.filledTimestamp;
              const CTS = pos.closeTransaction?.filledTimestamp;
              return (
                (OTS && new Date(OTS).getTime() === value) ||
                (CTS && new Date(CTS).getTime()) === value
              );
            });

            const positionLabel = position ? ` (${position._id})` : "";

            return longDate + positionLabel;
          },
          min: graphData.minTs,
          max: graphData.maxTs,
          tickInterval: graphData.ticks,
        },
      ]}
      series={[
        {
          curve: "linear",
          dataKey: "y",
          label: "Total Wallet Balance (USD)",
        },
      ]}
      dataset={graphData.dataset}
    />
  );
}
